import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useTenant } from '@/hooks/useTenant';
import type { CallLog, CallLogWithRelations, CTMRecordingResponse } from '@/types/calls';

// Hook for fetching call logs
export function useCallLogs(options: {
  leadId?: string;
  customerId?: string;
  limit?: number;
  enabled?: boolean;
}) {
  const { leadId, customerId, limit = 50, enabled = true } = options;

  return useQuery({
    queryKey: ['call-logs', { leadId, customerId }],
    queryFn: async (): Promise<CallLogWithRelations[]> => {
      let query = supabase
        .from('call_logs')
        .select(`
          *,
          leads:lead_id(customer_name, lead_number),
          customers:customer_id(customer_name),
          campaigns:campaign_id(name),
          profiles!call_logs_agent_id_fkey(full_name)
        `)
        .order('started_at', { ascending: false })
        .limit(limit);

      if (leadId) {
        query = query.eq('lead_id', leadId);
      }

      if (customerId) {
        query = query.eq('customer_id', customerId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as CallLogWithRelations[];
    },
    enabled: enabled && !!(leadId || customerId),
  });
}

// Hook for fetching a single call log
export function useCallLog(callId: string | undefined) {
  return useQuery({
    queryKey: ['call-log', callId],
    queryFn: async (): Promise<CallLogWithRelations> => {
      const { data, error } = await supabase
        .from('call_logs')
        .select(`
          *,
          leads:lead_id(customer_name, lead_number),
          customers:customer_id(customer_name),
          campaigns:campaign_id(name),
          profiles!call_logs_agent_id_fkey(full_name)
        `)
        .eq('id', callId!)
        .single();

      if (error) throw error;
      return data as CallLogWithRelations;
    },
    enabled: !!callId,
  });
}

// Hook for fetching call recording
export function useCallRecording(callId: string | undefined) {
  return useQuery({
    queryKey: ['call-recording', callId],
    queryFn: async (): Promise<CTMRecordingResponse> => {
      const { data, error } = await supabase.functions.invoke('ctm-get-recording', {
        method: 'POST',
        body: { call_id: callId },
      });

      if (error) throw error;
      return data;
    },
    enabled: false, // Only fetch when explicitly requested
    staleTime: 30 * 60 * 1000, // Recording URLs valid for ~1 hour
  });
}

// Hook for updating call disposition
export function useUpdateCallDisposition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ callId, disposition, notes }: { callId: string; disposition: string; notes?: string }) => {
      const { error } = await supabase
        .from('call_logs')
        .update({ disposition, notes })
        .eq('id', callId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['call-log', variables.callId] });
      queryClient.invalidateQueries({ queryKey: ['call-logs'] });
      toast({ title: 'Call disposition updated' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update call disposition',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Hook for company call tracking settings
export function useCallTrackingSettings() {
  const { company, isLoadingCompany } = useTenant();

  const data = useMemo(() => company ? {
    call_tracking_provider: company.call_tracking_provider,
    call_tracking_enabled: company.call_tracking_enabled,
    sms_enabled: company.sms_enabled,
    call_recording_enabled: company.call_recording_enabled,
    softphone_enabled: company.softphone_enabled,
  } : undefined, [company]);

  return { data, isLoading: isLoadingCompany };
}
